import {BarEvent, BarEventStatus, mergeEvents, responseTimeToEvents} from "@uptime-com/uptimebar";
import {intervalToDuration, parseISO} from "date-fns";

import {CheckDetailsAlert, LocationResponseTimes} from "@/lib/queries/checks.ts";
import {DataPoint} from "@/lib/types/checks.ts";
import {findMode} from "@/pages/checks/analysis/overview/utils.ts";

const MIN_INTERVAL = 5; //minimum interval of 5 minutes

//Net new function to combine data sources into a series of events
export const getEventsFromDataSources = ({
  responseTimes,
  intervalAlerts,
  interval,
  start,
  end,
  createdAt,
  timezone,
  noResponseTimesDefaultStatus = BarEventStatus.NO_DATA,
}: {
  start: Date;
  end: Date;
  createdAt: Date;
  interval: number;
  responseTimes: LocationResponseTimes[];
  intervalAlerts: CheckDetailsAlert[];
  timezone: string;
  noResponseTimesDefaultStatus?: BarEventStatus;
}): BarEvent[] => {
  // Generally this is just so an odd way to get this information. it would be much nicer if I could just get a series of events relating to no data, down, and maintenance
  // I don't think there are events for maintenance right now. Just an indicator on the check that it is currently in maintenance mode

  // get uptime events and no data events from response times

  const flattenPoints = responseTimes
    .reduce((acc: DataPoint[], loc) => acc.concat(loc.data), [])
    .map((p) => p.timestamp)
    .sort()
    .map((t) => parseISO(t));

  const stepsMs: number[] = [];
  for (let i = 0; i < flattenPoints.length; i++) {
    const current = flattenPoints[i];
    const next = flattenPoints[i + 1];
    if (current && next) {
      stepsMs.push(next.valueOf() - current.valueOf());
    }
  }

  const mode = findMode(stepsMs);
  const modeCount = stepsMs.filter((s) => s === mode).length;
  const stepModeMs = flattenPoints.length > 2 && modeCount > 1 && mode ? mode : undefined;
  const responseInterval = stepModeMs ? Math.ceil(stepModeMs / 60000) : interval;
  const finalInterval = responseInterval < MIN_INTERVAL ? MIN_INTERVAL : responseInterval;
  const durationGap = intervalToDuration({start: 0, end: finalInterval * 60 * 1000});
  const responses = responseTimeToEvents(
    start,
    end,
    flattenPoints,
    durationGap,
    createdAt,
    timezone,
    noResponseTimesDefaultStatus
  );

  // get the events from alerts
  const alerts: BarEvent[] = intervalAlerts
    .filter((e) => !e.ignored && !e.state_is_up)
    .map((e) => ({
      start: parseISO(e.created_at),
      end: e.ended_at ? parseISO(e.ended_at) : undefined,
      status: BarEventStatus.DOWN,
      details: e.output,
      id: e.id,
    }));

  return mergeEvents(responses, alerts) as BarEvent[];
};
